import React from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useWatchlist } from '../hooks/useWatchlist';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';

export function StockCard({ stock, loading = false, error = null }) {
  const { watchlistRows, addTicker, removeTicker } = useWatchlist();
  const sym = stock?.symbol?.toUpperCase();
  const isWatched = watchlistRows.some((r) => r.ticker === sym);

  const toggleWatchlist = (e) => {
    e.preventDefault();
    if (!stock?.symbol) return;
    if (isWatched) {
      removeTicker(stock.symbol);
    } else {
      addTicker(stock);
    }
  };

  const isNoData =
    stock?.noData ||
    stock?.status === 'Sin datos' ||
    (stock?.score === null && stock?.badge === 'gray');

  if (loading) {
    return (
      <Card className="p-5 animate-pulse">
        <div className="flex flex-col gap-4">
          <div>
            <div className="h-5 w-16 bg-gray-200 rounded mb-2" />
            <div className="h-3 w-32 bg-gray-100 rounded" />
          </div>
          <div className="flex justify-between items-end">
            <div>
              <div className="h-8 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-20 bg-gray-100 rounded" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="h-5 w-20 bg-gray-200 rounded-full" />
              <div className="h-3 w-14 bg-gray-100 rounded" />
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-5 border-red-100 bg-red-50">
        <div className="flex items-start gap-3">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-brand-red mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-brand-dark text-sm">
              {stock?.symbol || 'Error'}
            </p>
            <p className="text-xs text-red-700 mt-1 leading-snug">{error}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!stock) return null;

  return (
    <Link to={`/stock/${encodeURIComponent(stock.symbol)}`} className="block">
      <Card className="p-5 hover:border-brand-red/30 transition-colors group relative">
        <button
          type="button"
          onClick={toggleWatchlist}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-brand-red transition-colors z-10"
          title={isWatched ? 'Quitar de seguimiento' : 'Agregar a seguimiento'}
        >
          <SafeIcon
            icon={FiIcons.FiHeart}
            className={`text-xl transition-colors ${
              isWatched ? 'fill-brand-red text-brand-red' : ''
            }`}
          />
        </button>

        <div className="flex flex-col gap-4 pr-8">
          <div>
            <h3 className="text-lg font-bold text-brand-dark">{stock.symbol}</h3>
            <p className="text-sm text-brand-slate line-clamp-1">{stock.name}</p>
            {stock.errorMessage && (
              <p className="text-xs text-amber-700 mt-1">{stock.errorMessage}</p>
            )}
          </div>

          <div className="flex justify-between items-end">
            <div>
              <p className="text-2xl font-bold text-brand-dark">
                {formatCurrency(stock.price)}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500">Puntaje:</span>
                <span className="text-sm font-bold">
                  {stock.score != null && !isNoData ? `${stock.score}/100` : '—'}
                </span>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <Badge status={isNoData ? 'Sin datos' : stock.status} />
              <div className="text-xs font-medium text-gray-500">
                MOS:{' '}
                {stock.mos != null && !isNoData ? formatPercent(stock.mos) : '—'}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
